import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useAuthStore from '../../store/authStore';

const PERIODS = ['today', 'week', 'month', 'custom'];
const PERIOD_LABELS = { today: 'Today', week: 'This Week', month: 'This Month', custom: 'Custom' };

function StatCard({ label, value, color = '#2563eb', sub }) {
  return (
    <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb', flex: 1, minWidth: 160 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function OPDReports() {
  const token = useAuthStore(s => s.token);
  const headers = { Authorization: `Bearer ${token}` };

  const [activeTab, setActiveTab] = useState('daily');
  const [period, setPeriod] = useState('today');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') { params.from = dateFrom; params.to = dateTo; }
      const res = await axios.get('/api/opd-billing/report', { headers, params });
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, [period]);

  const exportCSV = () => {
    if (!data?.visits) return;
    const rows = [['Visit#', 'Date', 'Patient', 'MRN', 'Doctor', 'Department', 'Complaint', 'Payment Type', 'Fee', 'Paid', 'Balance', 'Status']];
    data.visits.forEach(v => rows.push([
      v.visit_number, new Date(v.created_at).toLocaleDateString('en-PK'), v.patient_name, v.mrn,
      v.doctor, v.department, v.chief_complaint, v.payment_type,
      v.visit_fee, v.advance_paid, v.balance_due, v.payment_status
    ]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `opd-report-${period}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { key: 'daily', label: 'Daily Summary' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'visits', label: 'Visit List' },
  ];

  return (
    <div style={{ padding: 24, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>OPD Reports</h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>Analytics and financial reports</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={exportCSV} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>Export CSV</button>
          <button onClick={() => window.print()} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>Print</button>
        </div>
      </div>

      {/* Period filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        {PERIODS.map(p => (
          <button key={p} onClick={() => setPeriod(p)} style={{
            padding: '7px 16px', border: `1px solid ${period === p ? '#2563eb' : '#d1d5db'}`,
            borderRadius: 8, cursor: 'pointer', fontSize: 13,
            backgroundColor: period === p ? '#eff6ff' : '#fff',
            color: period === p ? '#2563eb' : '#374151',
            fontWeight: period === p ? 600 : 400,
          }}>{PERIOD_LABELS[p]}</button>
        ))}
        {period === 'custom' && (
          <>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
            <span style={{ color: '#9ca3af' }}>to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
            <button onClick={fetch} style={{ padding: '7px 16px', border: 'none', borderRadius: 8, backgroundColor: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13 }}>Apply</button>
          </>
        )}
        {loading && <span style={{ fontSize: 13, color: '#9ca3af' }}>Loading...</span>}
      </div>

      {/* Stat cards */}
      {data?.summary && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <StatCard label="Total Visits" value={data.summary.total_visits || 0} color="#2563eb" />
          <StatCard label="Completed" value={data.summary.completed || 0} color="#16a34a" />
          <StatCard label="Total Revenue" value={`PKR ${parseFloat(data.summary.total_revenue || 0).toLocaleString('en-PK')}`} color="#7c3aed" />
          <StatCard label="Collected" value={`PKR ${parseFloat(data.summary.collected || 0).toLocaleString('en-PK')}`} color="#059669" sub="Advance paid" />
          <StatCard label="Pending" value={`PKR ${parseFloat(data.summary.pending || 0).toLocaleString('en-PK')}`} color="#dc2626" sub="Balance due" />
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e5e7eb' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            borderBottom: activeTab === t.key ? '2px solid #2563eb' : '2px solid transparent',
            backgroundColor: 'transparent', color: activeTab === t.key ? '#2563eb' : '#6b7280', marginBottom: -2
          }}>{t.label}</button>
        ))}
      </div>

      {/* Visit List */}
      {activeTab === 'visits' && data?.visits && (
        <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {data.visits.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No visits in this period.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  {['Visit #', 'Date', 'Patient', 'Doctor', 'Complaint', 'Payment', 'Fee', 'Paid', 'Balance', 'Status'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.visits.map(v => (
                  <tr key={v.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#2563eb' }}>{v.visit_number || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13 }}>{new Date(v.created_at).toLocaleDateString('en-PK')}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{v.patient_name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{v.mrn}</div>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 13 }}>{v.doctor || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13 }}>{v.chief_complaint || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13 }}>{v.payment_type || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13 }}>PKR {parseFloat(v.visit_fee || 0).toFixed(0)}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: '#059669' }}>PKR {parseFloat(v.advance_paid || 0).toFixed(0)}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: parseFloat(v.balance_due) > 0 ? '#dc2626' : '#6b7280' }}>PKR {parseFloat(v.balance_due || 0).toFixed(0)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                        backgroundColor: v.payment_status === 'PAID' ? '#f0fdf4' : v.payment_status === 'PARTIAL' ? '#fffbeb' : '#fef2f2',
                        color: v.payment_status === 'PAID' ? '#166534' : v.payment_status === 'PARTIAL' ? '#92400e' : '#991b1b' }}>
                        {v.payment_status || 'PENDING'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'daily' && data?.by_doctor && (
        <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', fontSize: 15, fontWeight: 700 }}>Visits by Doctor</div>
          {data.by_doctor.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No data available.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  {['Doctor', 'Total Visits', 'Completed', 'Revenue'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.by_doctor.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600 }}>{row.doctor || 'Unassigned'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14 }}>{row.total}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14 }}>{row.completed}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14 }}>PKR {parseFloat(row.revenue || 0).toLocaleString('en-PK')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'revenue' && data?.by_payment_type && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', fontSize: 15, fontWeight: 700 }}>By Payment Type</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  {['Type', 'Visits', 'Revenue'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.by_payment_type.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600 }}>{row.payment_type || 'Cash'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14 }}>{row.total}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14 }}>PKR {parseFloat(row.revenue || 0).toLocaleString('en-PK')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Collection Summary</div>
            {data.summary && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[['Total Billed', data.summary.total_revenue, '#111'], ['Collected (Advance)', data.summary.collected, '#16a34a'], ['Outstanding Balance', data.summary.pending, '#dc2626']].map(([label, val, color]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: 14 }}>{label}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color }}>PKR {parseFloat(val || 0).toLocaleString('en-PK')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
